import React, { useState, useEffect } from "react";
import styled from "styled-components";
import thumbsUp from "../img/thumbs-up.png";
import thumbsUpFilled from "../img/thumbs-up-filled.png";
import thumbsDown from "../img/thumbs-down.png";
import thumbsDownFilled from "../img/thumbs-down-filled.png";
import closeIcon from "../img/close_button.png";
import config from "../config.js";
import { apiFetch } from "../utils/apiClient.js";

const feedbackOptions = [
  { key: "NM", label: "판례의 내용이 내 상황과 맞지 않아요" },
  { key: "NUD", label: "법적 논리를 이해하기 어려워요" },
  { key: "OTD", label: "판례의 최신성이 부족해요" },
  { key: "MIS", label: "판례의 핵심 쟁점이 찾는 것과 달라요" },
  { key: "OTH", label: "기타" },
];

const CaseFeedback = ({ id, logId, isLike }) => {
  const [selectedThumb, setSelectedThumb] = useState(isLike);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false); // 피드백 제출 후 확인 메세지

  useEffect(() => {
    setSelectedThumb(isLike);
    if (isLike === "DISLIKE") {
      setShowOptions(false);
    }
  }, [isLike]);

  const sendFeedbackToServer = async (isLike, reason = "NONE") => {
    const SERVER_URI = config.serverUri;
    const uri = "/private/like";
    const url = `${SERVER_URI}${uri}`;

    const payload = {
      id: id,
      logId: logId,
      isLike: isLike,
      reason: reason, // 선택한 사유 포함 (없으면 "NONE")
    };

    try {
      const response = await apiFetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`전송 실패: HTTP 상태 코드 ${response.status}`);
      }

      const data = await response.json();
    } catch (error) {
      console.error("전송 중 오류 발생:", error);
    }
  };

  const handleThumbClick = (type) => {
    if (selectedThumb === type) {
      // 따봉 또는 비따봉 취소 → IsLike: "NONE", Reason: "NONE"
      setSelectedThumb("NONE");
      setSelectedOption(null);
      setShowOptions(false);
      setShowConfirmation(false);
      sendFeedbackToServer("NONE", "NONE");
    } else {
      if (type === "DISLIKE") {
        // 비따봉을 누르면 IsLike: "NONE", Reason: "NONE"
        setSelectedThumb("DISLIKE");
        setSelectedOption(null);
        setShowOptions(true);
        setShowConfirmation(false);
        sendFeedbackToServer("NONE", "NONE");
      } else {
        // 따봉을 누르면 IsLike: "LIKE", Reason: "NONE"
        setSelectedThumb("LIKE");
        setShowOptions(false);
        setShowConfirmation(false);
        sendFeedbackToServer("LIKE", "NONE");
      }
    }
  };

  const handleOptionClick = (optionKey) => {
    if (selectedOption === optionKey) {
      // 같은 옵션을 다시 클릭하면 사유 선택 해제 → IsLike: "NONE", Reason: "NONE"
      setSelectedOption(null);
      sendFeedbackToServer("NONE", "NONE");
    } else {
      // 새로운 사유 선택 → IsLike: "DISLIKE", Reason: (선택된 사유)
      setSelectedOption(optionKey);
      sendFeedbackToServer("DISLIKE", optionKey);

      // 비따봉 + 사유 선택된 1초 후, 사유 선택지(FeedbackBox)를 제출 완료 토스트(FeedbackConfirmation)로 변경
      setTimeout(() => {
        setShowOptions(false); // FeedbackBox 숨김
        setShowConfirmation(true); // FeedbackConfirmation 표시
      }, 1000);
    }
  };

  return (
    <Container>
      <FeedbackSection>
        <Text>해당 판례가 도움이 되었나요?</Text>
        <ThumbsIcon
          src={selectedThumb === "LIKE" ? thumbsUpFilled : thumbsUp}
          alt="좋아요"
          onClick={() => handleThumbClick("LIKE")}
        />
        <ThumbsIcon
          src={selectedThumb === "DISLIKE" ? thumbsDownFilled : thumbsDown}
          alt="싫어요"
          onClick={() => handleThumbClick("DISLIKE")}
        />
      </FeedbackSection>

      {selectedThumb === "DISLIKE" && (
        <>
          {showOptions ? (
            <>
              <FeedbackBox>
                <OptionHeader>
                  <OptionHeaderText>
                    <OptionTitle>어떤 이유로 도움이 되지 않았나요?</OptionTitle>
                    <OptionSubtitle>
                      (답변은 검색 질 향상을 위해 활용됩니다)
                    </OptionSubtitle>
                  </OptionHeaderText>
                  <CloseButton
                    src={closeIcon}
                    alt="닫기"
                    onClick={() => setShowOptions(false)}
                  />
                </OptionHeader>

                <OptionList>
                  {feedbackOptions.map((option) => (
                    <OptionButton
                      key={option.key}
                      selected={selectedOption === option.key}
                      onClick={() => handleOptionClick(option.key)}>
                      {option.label}
                    </OptionButton>
                  ))}
                </OptionList>
              </FeedbackBox>
            </>
          ) : showConfirmation ? ( // 사유 선택 후 1초 후 FeedbackConfirmation로 변경
            <FeedbackConfirmation>
              응답 완료되었습니다. 소중한 피드백 남겨주셔서 감사합니다.
            </FeedbackConfirmation>
          ) : (
            <FeedbackSpacer />
          )}
        </>
      )}
      <HeightGap />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  width: 680px; /* ResultForm 의 width 길이 가져옴 */

  @media (max-width: 1140px) and (min-width: 745px) {
    width: 680px;
  }

  @media (max-width: 744px) {
    width: 100%;
  }

  @media (max-width: 480px) {
    width: 100%;
    gap: 8px;
  }
`;

const FeedbackSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 744px) and (min-width: 481px) {
    margin: 0 32px;
  }
`;

const Text = styled.div`
  color: rgba(0, 0, 0, 0.5);
  text-align: right;
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px; /* 216.667% */
  letter-spacing: -0.24px;
`;

const ThumbsIcon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const FeedbackBox = styled.div`
  width: auto;
  height: 133px;
  padding: 13px 24px 20px 24px;
  border-radius: 12px;
  background: #f9f9f9;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 1140px) and (min-width: 745px) {
    height: 133px;
  }

  @media (max-width: 744px) and (min-width: 481px) {
    height: auto;
    margin: 0 32px;
  }

  @media (max-width: 480px) {
    height: auto;
    padding: 16px 20px;
  }
`;

const OptionHeaderText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-top: 3px;

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const OptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const OptionTitle = styled.span`
  color: rgba(0, 0, 0, 0.5);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; /* 216.667% */
  letter-spacing: -0.24px;
`;

const OptionSubtitle = styled.span`
  color: rgba(0, 0, 0, 0.3);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; /* 216.667% */
  letter-spacing: -0.24px;
`;

const CloseButton = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  cursor: pointer;
`;

const OptionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 13px;
`;

const OptionButton = styled.button`
  display: flex;
  width: 205px;
  height: 40px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  border: ${(props) =>
    props.selected ? "1px solid #1AE276" : "0.3px solid rgba(0, 0, 0, 0.20)"};
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  color: rgba(0, 0, 0, 0.5);
  text-align: right;
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; /* 216.667% */
  letter-spacing: -0.24px;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const HeightGap = styled.div`
  height: 32px;

  @media (max-width: 480px) {
    height: 24px;
  }
`;

const FeedbackSpacer = styled.div`
  height: 43px;

  @media (max-width: 480px) {
    height: 27px;
  }
`;

/* 제출 완료 토스트 */
const FeedbackConfirmation = styled.div`
  width: 680px;
  height: 50px;
  border-radius: 12px;
  border: 1px solid #1ae276;
  background: linear-gradient(
    90deg,
    rgba(26, 226, 118, 0.08) 33.51%,
    rgba(58, 125, 255, 0.08) 86.88%
  );

  /* 텍스트 CSS */
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  line-height: 50px;
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.24px;

  @media (max-width: 744px) and (min-width: 481px) {
    width: 90%; /* 임시방편으로 90%로 적용 */
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 52px;
    line-height: 52px;
  }
`;

export default CaseFeedback;
